/**
 * EKKO Trade Engine
 * 
 * A unified interface for all trading operations in the EKKO platform.
 * This engine abstracts the Jupiter Swap integration and provides a clean API
 * for executing various trade types.
 * 
 * Example usage with platform fees:
 * ```typescript
 * // Configure platform fees at the application level (typically in app initialization)
 * import { tradeEngine } from './services/tradeEngine';
 * 
 * // Set fee destination wallet (optional, defaults to EKKO fee address)
 * tradeEngine.configurePlatformFees('YourFeeDestinationWalletAddress');
 * 
 * // Later, execute trades without needing to specify fee info each time
 * const result = await tradeEngine.executeSwap({
 *   inputMint: SOL_MINT,
 *   outputMint: tokenMint,
 *   amount: '1.5',  // 1.5 SOL
 *   userWalletAddress: userWallet,
 *   tradingWalletAddress: tradingWallet,
 *   slippagePercent: '1.0'  // 1% slippage
 * });
 * ```
 */

// CRITICAL FIX: Polyfill for bigint compatibility issues with @solana/spl-token
// This fixes the "dist.from is not a function" error
if (typeof globalThis !== 'undefined' && !globalThis.Buffer) {
    globalThis.Buffer = require('buffer').Buffer;
}

// Ensure BigInt is available globally
if (typeof globalThis !== 'undefined' && typeof globalThis.BigInt === 'undefined') {
    globalThis.BigInt = BigInt;
}

import { PublicKey, LAMPORTS_PER_SOL, Transaction, SystemProgram, Connection } from '@solana/web3.js';
import {
    getJupiterQuote,
    getJupiterSwapTransaction,
    executeJupiterSwap,
    SOL_MINT
} from './jupiterService';
import { getBalance } from './shyftService';
import { getTokenBalance } from './solanatrackerService';
import { restoreTradingWalletKeypair } from './tradingWalletService';
import { getTokenInfoByMint } from './tokenRegistry';
import { tradeLoggingService, determineTradeMethod, getTokenAndMarketDataForLogging, type TradeLogEntry } from './tradeLoggingService';

// Import for the Connection and to properly handle sendAndConfirmTransaction
import { sendAndConfirmTransaction as solanaSignAndConfirmTransaction } from '@solana/web3.js';

// Import for getMint with error handling
let getMint: any;
try {
    const splToken = require('@solana/spl-token');
    getMint = splToken.getMint;
} catch (error) {
    console.warn('Failed to import getMint from @solana/spl-token:', error);
    // Create a fallback getMint function
    getMint = async (connection: Connection, mint: PublicKey) => {
        throw new Error('getMint not available - using fallback decimals');
    };
}

// Import Decimal.js for precise numeric calculations
import { Decimal } from 'decimal.js';

// Trading error types for better error handling
export enum TradeErrorType {
    INSUFFICIENT_BALANCE = 'INSUFFICIENT_BALANCE',
    TOKEN_NOT_FOUND = 'TOKEN_NOT_FOUND',
    SIMULATION_FAILED = 'SIMULATION_FAILED',
    SLIPPAGE_EXCEEDED = 'SLIPPAGE_EXCEEDED',
    TRANSACTION_FAILED = 'TRANSACTION_FAILED',
    WALLET_NOT_FOUND = 'WALLET_NOT_FOUND',
    NO_ROUTE_FOUND = 'NO_ROUTE_FOUND',
    UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

// Trade error class with type and context
export class TradeError extends Error {
    type: TradeErrorType;
    context?: any;

    constructor(message: string, type: TradeErrorType, context?: any) {
        super(message);
        this.name = 'TradeError';
        this.type = type;
        this.context = context;
    }
}

// Transaction status
export enum TransactionStatus {
    PENDING = 'PENDING',
    CONFIRMED = 'CONFIRMED',
    FAILED = 'FAILED'
}

// Trade result interface
export interface TradeResult {
    signature: string;
    status: TransactionStatus;
    inputAmount: string;
    actualInputAmount?: string; // Amount after fee deduction (for SOL inputs)
    inputMint: string;
    outputMint: string;
    estimatedOutputAmount?: string;
    estimatedOutputAfterFees?: string; // Estimated output after fees
    actualOutputAmount?: string;
    timestamp: number;
    fees?: {
        networkFee: number;
        platformFee?: number;
    };
    feeTransactionSignature?: string;
}

// Trade parameters interface
export interface TradeParams {
    // Basic trade info
    inputMint: string;
    outputMint: string;
    amount: string; // Input amount in natural units (e.g., SOL, not lamports)

    // User info
    userWalletAddress: string;
    tradingWalletAddress: string;
    userId?: string; // Optional user ID to avoid database lookup

    // Trade settings
    slippagePercent: string;
    priorityFee?: number;

    // Optional settings
    feeDestinationWallet?: string; // Wallet to receive platform fees
    platformFeePercent?: string; // Platform fee percentage, defaults to 0.25%
    referralWallet?: string;
    validateOnly?: boolean; // If true, just simulate but don't execute

    // Trade logging context
    tradeContext?: {
        isSniper?: boolean;
        isAutoTrader?: boolean;
        isCopyTrader?: boolean;
        isTokenCard?: boolean;
        isQuickSell?: boolean;
        ruleId?: string;
        ruleName?: string;
        metadata?: Record<string, any>;
    };
}

// Class implementation of the EKKO Trade Engine
export class EkkoTradeEngine {

    private static instance: EkkoTradeEngine;

    // Default platform fee percentage - TEMPORARILY DISABLED FOR SPEED
    private readonly DEFAULT_PLATFORM_FEE_PERCENT = '0.0';

    // App-level fee configuration
    private feeDestinationWallet: string = '2c2tkiuB7MpoZPvN5M9T6KtakWP9CajgjYLg5jDGHHTD';
    private platformFeePercent: string = '0.0'; // TEMPORARILY DISABLED - was 0.25%

    // 🚀 PERFORMANCE: Preferences cache for instant quick buys
    private preferencesCache = new Map<string, { data: any; timestamp: number }>();

    // Private constructor for singleton pattern
    private constructor() { }

    // Get singleton instance
    public static getInstance(): EkkoTradeEngine {
        if (!EkkoTradeEngine.instance) {
            EkkoTradeEngine.instance = new EkkoTradeEngine();
        }
        return EkkoTradeEngine.instance;
    }

    /**
     * Configure platform fees at the application level
     * @param feeDestinationWallet The wallet that will receive platform fees
     * @param platformFeePercent Optional fee percentage (defaults to 0.25%)
     */
    public configurePlatformFees(feeDestinationWallet: string, platformFeePercent?: string): void {
        if (!feeDestinationWallet) {
            throw new Error('Fee destination wallet is required');
        }

        // Validate the destination wallet is a valid public key
        try {
            new PublicKey(feeDestinationWallet);
        } catch (error) {
            throw new Error('Invalid fee destination wallet address');
        }

        this.feeDestinationWallet = feeDestinationWallet;

        // Update fee percentage if provided
        if (platformFeePercent) {
            const feePercent = parseFloat(platformFeePercent);
            if (isNaN(feePercent) || feePercent < 0 || feePercent > 100) {
                throw new Error('Platform fee percentage must be between 0 and 100');
            }
            this.platformFeePercent = platformFeePercent;
        }

        console.log(`EKKO Trade Engine: Platform fees configured - ${this.platformFeePercent}% to ${this.feeDestinationWallet}`);
    }

    /**
     * Get the current fee configuration
     */
    public getFeeConfiguration(): { feeDestinationWallet: string, platformFeePercent: string } {
        return {
            feeDestinationWallet: this.feeDestinationWallet,
            platformFeePercent: this.platformFeePercent
        };
    }

    /**
     * Executes a token swap
     * @param params Trade parameters
     * @returns Trade result
     */
    public async executeSwap(params: TradeParams): Promise<TradeResult> {
        try {
            console.log(`EKKO Trade Engine: Starting swap operation`, params);

            // Validate parameters
            this.validateTradeParams(params);

            // 🚀 PERFORMANCE OPTIMIZATION: Parallel processing for speed
            let enhancedParams = { ...params };

            // Start all async operations in parallel for maximum speed
            const parallelOperations = [];

            // 1. Balance check (always needed)
            parallelOperations.push(
                this.checkTradingWalletBalance(enhancedParams.tradingWalletAddress, enhancedParams)
            );

            // 2. User preferences (only if needed)
            let preferencesPromise: Promise<any> | null = null;
            if (params.tradeContext?.isTokenCard) {
                preferencesPromise = this.loadUserPreferencesOptimized(params.userWalletAddress);
                parallelOperations.push(preferencesPromise);
            } else if (params.tradeContext?.isQuickSell) {
                preferencesPromise = this.loadQuickSellPreferencesOptimized(params.userWalletAddress);
                parallelOperations.push(preferencesPromise);
            }

            // Execute all operations in parallel
            console.log(`🚀 Starting ${parallelOperations.length} parallel operations for maximum speed`);
            const startTime = performance.now();

            try {
                await Promise.all(parallelOperations);
                const endTime = performance.now();
                console.log(`✅ All parallel operations completed in ${(endTime - startTime).toFixed(1)}ms`);
            } catch (error) {
                console.warn('⚠️ Some parallel operations failed, continuing with defaults:', error);
            }

            // 3. Apply user preferences if loaded successfully
            if (preferencesPromise) {
                try {
                    const userPrefs = await preferencesPromise;
                    enhancedParams = this.applyUserPreferences(enhancedParams, userPrefs);
                } catch (prefError) {
                    console.warn('❌ Could not load user preferences, using provided values:', prefError);
                }
            }

            // Create a new params object with app-level fee settings
            const paramsWithFees: TradeParams = {
                ...enhancedParams,
                // Use app-level fee percentage if not explicitly overridden
                platformFeePercent: enhancedParams.platformFeePercent || this.platformFeePercent
            };

            // Handle different input token types
            if (paramsWithFees.inputMint === SOL_MINT) {
                return this.executeSolToTokenSwap(paramsWithFees);
            } else if (paramsWithFees.outputMint === SOL_MINT) {
                return this.executeTokenToSolSwap(paramsWithFees);
            } else {
                return this.executeTokenToTokenSwap(paramsWithFees);
            }
        } catch (error) {
            console.error('EKKO Trade Engine: Swap execution failed', error);

            // Classify error type
            if (error instanceof TradeError) {
                throw error;
            }

            const message = error instanceof Error ? error.message : 'Unknown error during swap';
            throw new TradeError(message, TradeErrorType.UNKNOWN_ERROR, error);
        }
    }

    /**
     * 🚀 OPTIMIZED: Load user preferences with aggressive caching and parallel queries
     */
    private async loadUserPreferencesOptimized(userWalletAddress: string): Promise<any> {
        const cacheKey = `user_prefs_${userWalletAddress}`;
        const cached = this.preferencesCache.get(cacheKey);

        if (cached && (Date.now() - cached.timestamp) < 30000) { // 30 second cache
            console.log(`📋 Using cached user preferences (${Date.now() - cached.timestamp}ms old)`);
            return cached.data;
        }

        try {
            console.log(`🎯 Loading user preferences for: ${userWalletAddress}`);
            const { getQuickBuyPreferences } = await import('./quickBuyService');
            const userPrefs = await getQuickBuyPreferences(userWalletAddress);

            // Cache the result
            this.preferencesCache.set(cacheKey, {
                data: userPrefs,
                timestamp: Date.now()
            });

            return userPrefs;
        } catch (error) {
            console.warn('Failed to load user preferences:', error);
            // Return sensible defaults
            return { amount: '0.1', slippage: '1' };
        }
    }

    /**
     * 🚀 OPTIMIZED: Load quick sell preferences with caching
     */
    private async loadQuickSellPreferencesOptimized(userWalletAddress: string): Promise<any> {
        const cacheKey = `quick_sell_prefs_${userWalletAddress}`;
        const cached = this.preferencesCache.get(cacheKey);

        if (cached && (Date.now() - cached.timestamp) < 30000) { // 30 second cache
            console.log(`📋 Using cached quick sell preferences (${Date.now() - cached.timestamp}ms old)`);
            return cached.data;
        }

        try {
            const { getActivePositionsQuickSellPreferences } = await import('./userProfileService');
            const sellPrefs = await getActivePositionsQuickSellPreferences(userWalletAddress);

            // Cache the result
            this.preferencesCache.set(cacheKey, {
                data: sellPrefs,
                timestamp: Date.now()
            });

            return sellPrefs;
        } catch (error) {
            console.warn('Failed to load quick sell preferences:', error);
            return { slippagePercent: 1 };
        }
    }

    /**
     * 🚀 OPTIMIZED: Apply user preferences without blocking
     */
    private applyUserPreferences(params: TradeParams, userPrefs: any): TradeParams {
        const enhanced = { ...params };

        if (params.tradeContext?.isTokenCard) {
            console.log(`📋 Original params - amount: ${params.amount}, slippage: ${params.slippagePercent}%`);

            // Override amount if not explicitly set and this is a SOL->Token swap
            if (params.inputMint === SOL_MINT && !params.tradeContext?.metadata?.customAmount) {
                const oldAmount = enhanced.amount;
                enhanced.amount = userPrefs.amount;
                console.log(`💰 AMOUNT OVERRIDE: ${oldAmount} → ${userPrefs.amount} SOL`);
            }

            // Override slippage if not explicitly set
            if (!params.tradeContext?.metadata?.customSlippage) {
                const oldSlippage = enhanced.slippagePercent;
                enhanced.slippagePercent = userPrefs.slippage;
                console.log(`📊 SLIPPAGE OVERRIDE: ${oldSlippage}% → ${userPrefs.slippage}%`);
            }
        } else if (params.tradeContext?.isQuickSell) {
            // Override slippage if not explicitly set
            if (!params.tradeContext?.metadata?.customSlippage) {
                enhanced.slippagePercent = userPrefs.slippagePercent.toString();
                console.log(`📊 Using user's quick sell slippage: ${userPrefs.slippagePercent}%`);
            }
        }

        return enhanced;
    }

    /**
     * Handle common Jupiter swap errors and convert them to user-friendly TradeErrors
     */
    private handleSwapError(error: any): never {
        console.error(`EKKO Trade Engine: Swap execution failed`, error);

        // Get the error message
        const message = error instanceof Error ? error.message : 'Unknown error during swap';

        // Check for specific "Pool is completed" error (error code 0x177d or 6013)
        if (message.includes('custom program error: 0x177d') ||
            message.includes('Error Code: PoolIsCompleted') ||
            message.includes('Pool is completed')) {

            throw new TradeError(
                `This token has completed its bonding curve phase. Please use a DEX like Raydium or Jupiter directly to trade this token.`,
                TradeErrorType.NO_ROUTE_FOUND,
                error
            );
        }

        // Check for slippage exceeded errors
        if (message.includes('0x1771') ||
            message.includes('slippage tolerance') ||
            message.includes('Slippage tolerance')) {
            throw new TradeError(
                `Slippage tolerance exceeded. Try increasing slippage or using a smaller amount.`,
                TradeErrorType.SLIPPAGE_EXCEEDED,
                error
            );
        }

        // Check for insufficient balance errors
        if (message.includes('insufficient') &&
            (message.includes('lamports') || message.includes('balance') || message.includes('funds'))) {
            throw new TradeError(
                `Insufficient balance for this transaction.`,
                TradeErrorType.INSUFFICIENT_BALANCE,
                error
            );
        }

        // Check for liquidity errors
        if (message.includes('liquidity') ||
            message.includes('No route found') ||
            message.includes('No route available') ||
            message.includes('COULD_NOT_FIND_ANY_ROUTE')) {
            throw new TradeError(
                `Insufficient liquidity or no trading route available for this token.`,
                TradeErrorType.NO_ROUTE_FOUND,
                error
            );
        }

        // Check for Token not found errors
        if (message.includes('Token not found in Jupiter')) {
            throw new TradeError(
                `This token is too new or not recognized by Jupiter.`,
                TradeErrorType.TOKEN_NOT_FOUND,
                error
            );
        }

        // Handle TOKEN_NOT_TRADABLE error
        if (message.includes('TOKEN_NOT_TRADABLE')) {
            throw new TradeError(
                `This token cannot be traded on Jupiter at this time. It may be too new, have low liquidity, or be restricted from trading.`,
                TradeErrorType.TOKEN_NOT_FOUND,
                error
            );
        }

        // Handle missing token metadata errors
        if (message.includes('missing token decimals') ||
            message.includes('TOKEN_METADATA_MISSING')) {
            throw new TradeError(
                `This token is too new and Jupiter is still indexing its metadata. Please try again in a few minutes.`,
                TradeErrorType.TOKEN_NOT_FOUND,
                error
            );
        }

        // Default error handling
        throw new TradeError(
            `Failed to execute swap: ${message}`,
            TradeErrorType.TRANSACTION_FAILED,
            error
        );
    }

    /**
     * Execute a swap from SOL to a token
     * For this case, we send 0.25% to fee wallet, then swap the remainder
     */
    private async executeSolToTokenSwap(params: TradeParams): Promise<TradeResult> {
        console.log(`EKKO Trade Engine: Executing SOL → Token swap`);

        // FEES TEMPORARILY DISABLED FOR SPEED - Calculate fee amount in SOL
        const feePercent = '0.0'; // Force 0% fee for speed
        const feeMultiplier = 0;
        const totalSolAmount = parseFloat(params.amount);
        const feeAmountInSol = 0; // No fees

        // Use full amount for swap (no fee deduction)
        const swapAmountInSol = totalSolAmount;

        // Convert to lamports
        const swapAmountInLamports = Math.floor(swapAmountInSol * LAMPORTS_PER_SOL);
        const feeAmountInLamports = Math.floor(feeAmountInSol * LAMPORTS_PER_SOL);

        // Convert slippage percentage to basis points (1% = 100 bps)
        const slippageBps = (parseFloat(params.slippagePercent) * 100).toString();

        try {
            // 0. Restore the trading wallet keypair
            const tradingWalletKeypair = await restoreTradingWalletKeypair(
                params.userWalletAddress,
                params.tradingWalletAddress
            );

            if (!tradingWalletKeypair) {
                throw new TradeError(
                    `Could not restore keypair for trading wallet: ${params.tradingWalletAddress}`,
                    TradeErrorType.WALLET_NOT_FOUND
                );
            }

            // 1. Get quote for the swap amount (not including fee)
            const quoteResponse = await getJupiterQuote({
                inputMint: SOL_MINT,
                outputMint: params.outputMint,
                amount: swapAmountInLamports.toString(),
                slippageBps
            });

            // Extract estimated output amount from quote
            const estimatedOutputAmount = this.formatTokenAmount(quoteResponse.outAmount);

            // 2. Get swap transaction
            const swapParams: any = {
                quoteResponse,
                userPublicKey: params.tradingWalletAddress,
                wrapAndUnwrapSol: true
            };

            // Add priority fee if specified
            if (params.priorityFee && params.priorityFee > 0) {
                swapParams.computeUnitPriceMicroLamports = params.priorityFee;
            }

            // Get swap transaction
            const { swapTransaction } = await getJupiterSwapTransaction(swapParams);

            // If validate only, don't execute
            if (params.validateOnly) {
                return {
                    signature: 'simulation-only',
                    status: TransactionStatus.PENDING,
                    inputAmount: params.amount,
                    actualInputAmount: swapAmountInSol.toString(), // Full amount (no fees)
                    inputMint: params.inputMint,
                    outputMint: params.outputMint,
                    estimatedOutputAmount,
                    fees: {
                        networkFee: 0.000005, // approximate
                        platformFee: 0 // Platform fees completely removed
                    },
                    timestamp: Date.now()
                };
            }

            // 3. Execute the swap transaction
            let signature;
            try {
                const result = await executeJupiterSwap(
                    swapTransaction,
                    params.userWalletAddress,
                    params.tradingWalletAddress
                );
                signature = result.signature;

                // Log successful confirmation (executeJupiterSwap now waits for confirmation)
                console.log(`EKKO Trade Engine: Transaction confirmed successfully with signature ${signature}`);
            } catch (error) {
                // Use our handleSwapError method to create user-friendly error messages
                return this.handleSwapError(error);
            }

            // Trigger immediate balance refresh for this wallet
            this.triggerBalanceRefresh(params.tradingWalletAddress);

            // 4. Platform fees completely removed for maximum speed

            // 5. Build and return the result
            const result: TradeResult = {
                signature,
                status: TransactionStatus.CONFIRMED,
                inputAmount: params.amount,
                actualInputAmount: swapAmountInSol.toString(), // Full amount used (no fees)
                inputMint: params.inputMint,
                outputMint: params.outputMint,
                estimatedOutputAmount,
                fees: {
                    networkFee: 0.000005, // Approximate network fee in SOL
                    platformFee: 0 // Platform fees completely removed
                },
                timestamp: Date.now()
            };

            console.log(`EKKO Trade Engine: SOL → Token swap completed:
            - Swap (${swapAmountInSol.toFixed(9)} SOL → Token) executed with signature ${signature}
            - Platform fees completely removed for maximum speed`);

            // Log the trade to unified trades table
            this.logTrade(params, result);

            return result;
        } catch (error) {
            // Use our handleSwapError method for consistent error handling
            return this.handleSwapError(error);
        }
    }

    // Platform fee functionality completely removed for maximum speed

    /**
     * Execute a swap from a token to SOL
     * For this case, we simply deduct the fee from the expected output SOL amount for reporting
     */
    private async executeTokenToSolSwap(params: TradeParams): Promise<TradeResult> {
        console.log(`EKKO Trade Engine: Executing Token → SOL swap`);

        try {
            // CRITICAL FIX: Properly get token decimals with better error handling and fallbacks
            let tokenDecimals: number = 9; // Default to 9 decimals (most common)

            try {
                // Try to get token decimals directly from blockchain
                const connection = new Connection('https://rpc.shyft.to?api_key=cvnR7P83T858ITni', 'confirmed');
                const mintInfo = await getMint(connection, new PublicKey(params.inputMint));
                tokenDecimals = mintInfo.decimals;
                console.log(`[FIXED] Token ${params.inputMint} has ${tokenDecimals} decimals (from blockchain)`);
            } catch (blockchainError) {
                console.warn('Error fetching token decimals from blockchain:', blockchainError);

                // Fallback 1: Try to get from token info service
                try {
                    const { getTokenInfo } = await import('./solanatrackerService');
                    const tokenInfo = await getTokenInfo(params.inputMint);

                    if (tokenInfo && tokenInfo.status === 'success' && tokenInfo.data?.decimals !== undefined) {
                        tokenDecimals = tokenInfo.data.decimals;
                        console.log(`[FALLBACK] Token ${params.inputMint} has ${tokenDecimals} decimals (from token info)`);
                    } else {
                        console.warn(`[WARNING] Could not get decimals from token info, using default: ${tokenDecimals}`);
                    }
                } catch (tokenInfoError) {
                    console.warn(`[WARNING] Token info service failed, using default decimals: ${tokenDecimals}`, tokenInfoError);
                }
            }

            // 🚀 OPTIMIZED: Fast balance check with parallel fallbacks (no delays)
            let tokenBalance = '0';

            try {
                // Try all methods in parallel for maximum speed
                const balancePromises = [
                    // Method 1: SolanaTracker service (primary)
                    getTokenBalance(params.tradingWalletAddress, params.inputMint, true),

                    // Method 2: Refresh wallet tokens (parallel)
                    (async () => {
                        try {
                            const { refreshWalletTokens } = await import('./solanatrackerService');
                            const refreshResult = await refreshWalletTokens(params.tradingWalletAddress, params.inputMint, true);
                            if (refreshResult.status === 'success' && refreshResult.data?.tokens) {
                                const targetToken = refreshResult.data.tokens.find((t: any) => t.mint === params.inputMint);
                                return targetToken?.balance || '0';
                            }
                            return '0';
                        } catch {
                            return '0';
                        }
                    })()
                ];

                // Wait for the first successful result
                const results = await Promise.allSettled(balancePromises);

                // Find the first non-zero balance
                for (const result of results) {
                    if (result.status === 'fulfilled' && result.value && result.value !== '0') {
                        tokenBalance = result.value;
                        console.log(`✅ Found balance via parallel check: ${tokenBalance}`);
                        break;
                    }
                }

                console.log(`🚀 Balance check completed in parallel - found: ${tokenBalance}`);

            } catch (balanceError) {
                console.error(`Balance check failed:`, balanceError);
            }

            // Debug logs to track the issue
            console.log(`ORIGINAL AMOUNT RECEIVED: ${params.amount} (${typeof params.amount})`);
            console.log(`Token balance found: ${tokenBalance}`);
            console.log(`Using token decimals: ${tokenDecimals} for token ${params.inputMint}`);

            // IMPROVED: More robust balance check with better error messaging
            const tokenBalanceNum = parseFloat(tokenBalance);
            const amountNum = parseFloat(params.amount);

            if (tokenBalanceNum <= 0) {
                // If we still have no balance after all attempts, provide helpful error
                throw new TradeError(
                    `No token balance found for ${params.inputMint}. Please ensure you have tokens in your trading wallet or try refreshing your wallet.`,
                    TradeErrorType.INSUFFICIENT_BALANCE
                );
            }

            // Allow for tiny differences (0.000001 tokens) to handle precision issues
            const PRECISION_TOLERANCE = 0.000001;
            const balanceDeficit = amountNum - tokenBalanceNum;

            if (balanceDeficit > PRECISION_TOLERANCE) {
                throw new TradeError(
                    `Insufficient token balance. Have ${tokenBalanceNum.toFixed(6)}, need ${amountNum.toFixed(6)}`,
                    TradeErrorType.INSUFFICIENT_BALANCE
                );
            }

            // If we're trying to sell more than we have but within tolerance, adjust to exact balance
            let adjustedAmount = params.amount;
            if (balanceDeficit > 0 && balanceDeficit <= PRECISION_TOLERANCE) {
                adjustedAmount = tokenBalance; // Use exact balance string to avoid precision loss
                console.log(`🔧 PRECISION FIX: Adjusted sell amount from ${params.amount} to ${adjustedAmount} (exact balance)`);
            }

            // Calculate fee percentage
            const feePercent = params.platformFeePercent || this.platformFeePercent;
            const feeMultiplier = parseFloat(feePercent) / 100;

            // Convert slippage percentage to basis points (1% = 100 bps)
            const slippageBps = (parseFloat(params.slippagePercent) * 100).toString();

            // CORRECT CONVERSION: Jupiter expects an integer in the smallest units (base units)
            // Use Decimal.js for precise conversion with the correct number of decimals
            const rawAmountString = adjustedAmount;
            let amountInSmallestUnits: string;

            // Import the toAtomicUnits function from jupiterService dynamically
            const { toAtomicUnits } = await import('./jupiterService');
            amountInSmallestUnits = toAtomicUnits(rawAmountString, tokenDecimals);
            console.log(`ATOMIC CONVERSION: ${rawAmountString} → ${amountInSmallestUnits} (${tokenDecimals} decimals)`);

            // Double-check our calculation
            try {
                const verificationAmount = new Decimal(rawAmountString).mul(new Decimal(10).pow(tokenDecimals)).toString();
                console.log(`VERIFICATION: ${rawAmountString} × 10^${tokenDecimals} = ${verificationAmount}`);

                // If our atomic units don't match verification, something is wrong
                if (amountInSmallestUnits !== verificationAmount) {
                    console.error(`ERROR: Atomic conversion mismatch. Expected ${verificationAmount}, got ${amountInSmallestUnits}`);
                }
            } catch (verifyError) {
                console.warn('Verification calculation error:', verifyError);
            }

            console.log(`Final amount in smallest units for Jupiter: ${amountInSmallestUnits}`);

            // Get quote for selling token to SOL
            const quoteResponse = await getJupiterQuote({
                inputMint: params.inputMint,
                outputMint: SOL_MINT,
                amount: amountInSmallestUnits,
                slippageBps
            });

            // Calculate expected output in SOL
            const estimatedOutputInLamports = quoteResponse.outAmount;
            const estimatedOutputInSol = parseFloat(estimatedOutputInLamports) / LAMPORTS_PER_SOL;

            // Calculate fee amount in SOL
            const feeAmountInSol = estimatedOutputInSol * feeMultiplier;

            // Format the estimated output after fees
            const outputAfterFees = estimatedOutputInSol - feeAmountInSol;
            const formattedOutputAfterFees = outputAfterFees.toFixed(9);

            // 2. Get swap transaction
            const swapParams: any = {
                quoteResponse,
                userPublicKey: params.tradingWalletAddress,
                wrapAndUnwrapSol: true
            };

            // Add priority fee if specified
            if (params.priorityFee && params.priorityFee > 0) {
                swapParams.computeUnitPriceMicroLamports = params.priorityFee;
            }

            // Get swap transaction
            const { swapTransaction } = await getJupiterSwapTransaction(swapParams);

            // If validate only, don't execute
            if (params.validateOnly) {
                return {
                    signature: 'simulation-only',
                    status: TransactionStatus.PENDING,
                    inputAmount: adjustedAmount, // Use adjusted amount
                    inputMint: params.inputMint,
                    outputMint: params.outputMint,
                    estimatedOutputAmount: formattedOutputAfterFees,
                    fees: {
                        networkFee: 0.000005, // approximate
                        platformFee: feeAmountInSol
                    },
                    timestamp: Date.now()
                };
            }

            // 0. Restore the trading wallet keypair for subsequent fee transaction
            const tradingWalletKeypair = await restoreTradingWalletKeypair(
                params.userWalletAddress,
                params.tradingWalletAddress
            );

            if (!tradingWalletKeypair) {
                throw new TradeError(
                    `Could not restore keypair for trading wallet: ${params.tradingWalletAddress}`,
                    TradeErrorType.WALLET_NOT_FOUND
                );
            }

            // 3. Execute the swap transaction
            let signature;
            try {
                const result = await executeJupiterSwap(
                    swapTransaction,
                    params.userWalletAddress,
                    params.tradingWalletAddress
                );
                signature = result.signature;

                // Log successful confirmation (executeJupiterSwap now waits for confirmation)
                console.log(`EKKO Trade Engine: Token → SOL transaction confirmed successfully with signature ${signature}`);
            } catch (error) {
                // Use our handleSwapError method to create user-friendly error messages
                return this.handleSwapError(error);
            }

            // Trigger immediate balance refresh for this wallet
            this.triggerBalanceRefresh(params.tradingWalletAddress);

            // Calculate fee in lamports
            const feeAmountInLamports = Math.floor(feeAmountInSol * LAMPORTS_PER_SOL);

            // Platform fees completely removed for maximum speed

            // 4. Build and return the result
            const result: TradeResult = {
                signature,
                status: TransactionStatus.CONFIRMED,
                inputAmount: adjustedAmount, // Use adjusted amount
                inputMint: params.inputMint,
                outputMint: params.outputMint,
                estimatedOutputAmount: estimatedOutputInSol.toString(),
                estimatedOutputAfterFees: formattedOutputAfterFees, // Add estimated output after fees
                fees: {
                    networkFee: 0.000005, // Approximate network fee in SOL
                    platformFee: feeAmountInSol
                },
                timestamp: Date.now()
            };

            console.log(`EKKO Trade Engine: Token → SOL swap completed. Platform fee of ${feeAmountInSol.toFixed(9)} SOL (${feePercent}%) will be sent to ${this.feeDestinationWallet} in background.`);

            // Log the trade to unified trades table
            this.logTrade(params, result);

            return result;
        } catch (error) {
            // Use our handleSwapError method for consistent error handling
            return this.handleSwapError(error);
        }
    }

    /**
     * Execute a swap from one token to another token
     * For this case, we just calculate the theoretical fee for reporting purposes
     */
    private async executeTokenToTokenSwap(params: TradeParams): Promise<TradeResult> {
        console.log(`EKKO Trade Engine: Executing Token → Token swap`);

        try {
            // CRITICAL FIX: Properly get token decimals with better error handling and fallbacks
            let tokenDecimals: number = 9; // Default to 9 decimals (most common)

            try {
                // Try to get token decimals directly from blockchain
                const connection = new Connection('https://rpc.shyft.to?api_key=cvnR7P83T858ITni', 'confirmed');
                const mintInfo = await getMint(connection, new PublicKey(params.inputMint));
                tokenDecimals = mintInfo.decimals;
                console.log(`[FIXED] Token ${params.inputMint} has ${tokenDecimals} decimals (from blockchain)`);
            } catch (blockchainError) {
                console.warn('Error fetching token decimals from blockchain:', blockchainError);

                // Fallback 1: Try to get from token info service
                try {
                    const { getTokenInfo } = await import('./solanatrackerService');
                    const tokenInfo = await getTokenInfo(params.inputMint);

                    if (tokenInfo && tokenInfo.status === 'success' && tokenInfo.data?.decimals !== undefined) {
                        tokenDecimals = tokenInfo.data.decimals;
                        console.log(`[FALLBACK] Token ${params.inputMint} has ${tokenDecimals} decimals (from token info)`);
                    } else {
                        console.warn(`[WARNING] Could not get decimals from token info, using default: ${tokenDecimals}`);
                    }
                } catch (tokenInfoError) {
                    console.warn(`[WARNING] Token info service failed, using default decimals: ${tokenDecimals}`, tokenInfoError);
                }
            }

            // 🚀 OPTIMIZED: Fast balance check with parallel fallbacks (no delays)
            let tokenBalance = '0';

            try {
                // Try all methods in parallel for maximum speed
                const balancePromises = [
                    // Method 1: SolanaTracker service (primary)
                    getTokenBalance(params.tradingWalletAddress, params.inputMint, true),

                    // Method 2: Refresh wallet tokens (parallel)
                    (async () => {
                        try {
                            const { refreshWalletTokens } = await import('./solanatrackerService');
                            const refreshResult = await refreshWalletTokens(params.tradingWalletAddress, params.inputMint, true);
                            if (refreshResult.status === 'success' && refreshResult.data?.tokens) {
                                const targetToken = refreshResult.data.tokens.find((t: any) => t.mint === params.inputMint);
                                return targetToken?.balance || '0';
                            }
                            return '0';
                        } catch {
                            return '0';
                        }
                    })()
                ];

                // Wait for the first successful result
                const results = await Promise.allSettled(balancePromises);

                // Find the first non-zero balance
                for (const result of results) {
                    if (result.status === 'fulfilled' && result.value && result.value !== '0') {
                        tokenBalance = result.value;
                        console.log(`✅ Found balance via parallel check: ${tokenBalance}`);
                        break;
                    }
                }

                console.log(`🚀 Balance check completed in parallel - found: ${tokenBalance}`);

            } catch (balanceError) {
                console.error(`Balance check failed:`, balanceError);
            }

            console.log(`Using token decimals: ${tokenDecimals} for token ${params.inputMint}`);
            console.log(`Token balance found: ${tokenBalance}`);

            // IMPROVED: More robust balance check with better error messaging
            const tokenBalanceNum = parseFloat(tokenBalance);
            const amountNum = parseFloat(params.amount);

            if (tokenBalanceNum <= 0) {
                // If we still have no balance after all attempts, provide helpful error
                throw new TradeError(
                    `No token balance found for ${params.inputMint}. Please ensure you have tokens in your trading wallet or try refreshing your wallet.`,
                    TradeErrorType.INSUFFICIENT_BALANCE
                );
            }

            // Allow for tiny differences (0.000001 tokens) to handle precision issues
            const PRECISION_TOLERANCE = 0.000001;
            const balanceDeficit = amountNum - tokenBalanceNum;

            if (balanceDeficit > PRECISION_TOLERANCE) {
                throw new TradeError(
                    `Insufficient token balance. Have ${tokenBalanceNum.toFixed(6)}, need ${amountNum.toFixed(6)}`,
                    TradeErrorType.INSUFFICIENT_BALANCE
                );
            }

            // If we're trying to sell more than we have but within tolerance, adjust to exact balance
            let adjustedAmount = params.amount;
            if (balanceDeficit > 0 && balanceDeficit <= PRECISION_TOLERANCE) {
                adjustedAmount = tokenBalance; // Use exact balance string to avoid precision loss
                console.log(`🔧 PRECISION FIX: Adjusted swap amount from ${params.amount} to ${adjustedAmount} (exact balance)`);
            }

            // Calculate fee percentage
            const feePercent = params.platformFeePercent || this.platformFeePercent;
            const feeMultiplier = parseFloat(feePercent) / 100;

            // Convert slippage percentage to basis points (1% = 100 bps)
            const slippageBps = (parseFloat(params.slippagePercent) * 100).toString();

            // CORRECT CONVERSION: For proper atomic units conversion
            // Import the toAtomicUnits function from jupiterService dynamically
            const { toAtomicUnits } = await import('./jupiterService');
            const amountInSmallestUnits = toAtomicUnits(adjustedAmount, tokenDecimals);

            console.log(`ATOMIC CONVERSION: ${adjustedAmount} → ${amountInSmallestUnits} (${tokenDecimals} decimals)`);

            // 1. Get quote
            const quoteResponse = await getJupiterQuote({
                inputMint: params.inputMint,
                outputMint: params.outputMint,
                amount: amountInSmallestUnits,
                slippageBps
            });

            // Extract estimated output amount from quote
            const estimatedOutputAmount = this.formatTokenAmount(quoteResponse.outAmount);

            // Calculate approximate fee (this is just for reporting)
            // For token-to-token, we estimate a SOL fee based on trade size
            const approximateFeeValue = 0.005 * feeMultiplier; // This is an approximate SOL value of the fee

            // 2. Get swap transaction
            const swapParams: any = {
                quoteResponse,
                userPublicKey: params.tradingWalletAddress,
                wrapAndUnwrapSol: true
            };

            // Add priority fee if specified
            if (params.priorityFee && params.priorityFee > 0) {
                swapParams.computeUnitPriceMicroLamports = params.priorityFee;
            }

            // Get swap transaction
            const { swapTransaction } = await getJupiterSwapTransaction(swapParams);

            // If validate only, don't execute
            if (params.validateOnly) {
                return {
                    signature: 'simulation-only',
                    status: TransactionStatus.PENDING,
                    inputAmount: adjustedAmount, // Use adjusted amount
                    inputMint: params.inputMint,
                    outputMint: params.outputMint,
                    estimatedOutputAmount,
                    fees: {
                        networkFee: 0.000005, // approximate
                        platformFee: approximateFeeValue
                    },
                    timestamp: Date.now()
                };
            }

            // 3. Execute the swap transaction
            let signature;
            try {
                const result = await executeJupiterSwap(
                    swapTransaction,
                    params.userWalletAddress,
                    params.tradingWalletAddress
                );
                signature = result.signature;

                // Log successful confirmation (executeJupiterSwap now waits for confirmation)
                console.log(`EKKO Trade Engine: Token → Token transaction confirmed successfully with signature ${signature}`);
            } catch (error) {
                // Use our handleSwapError method to create user-friendly error messages
                return this.handleSwapError(error);
            }

            // Trigger immediate balance refresh for this wallet
            this.triggerBalanceRefresh(params.tradingWalletAddress);

            // 4. Build and return the result
            const result: TradeResult = {
                signature,
                status: TransactionStatus.CONFIRMED,
                inputAmount: adjustedAmount, // Use adjusted amount
                inputMint: params.inputMint,
                outputMint: params.outputMint,
                estimatedOutputAmount,
                fees: {
                    networkFee: 0.000005, // Approximate network fee in SOL
                    platformFee: approximateFeeValue
                },
                timestamp: Date.now()
            };

            console.log(`EKKO Trade Engine: Token → Token swap completed. Note: Platform fee would need to be collected separately.`);

            // Log the trade to unified trades table
            this.logTrade(params, result);

            return result;
        } catch (error) {
            // Use our handleSwapError method for consistent error handling
            return this.handleSwapError(error);
        }
    }

    /**
     * Get a price quote without executing a swap
     * @param params Trade parameters
     * @returns Estimated output amount and other quote details
     */
    public async getQuote(params: Omit<TradeParams, 'userWalletAddress' | 'tradingWalletAddress'>): Promise<{
        inputMint: string;
        outputMint: string;
        inputAmount: string;
        swapAmount: string;
        estimatedOutputAmount: string;
        estimatedFeeAmount: string;
        priceImpact: string;
        platformFeePercent?: string;
        routes: any[];
    }> {
        try {
            // Add app-level fee percentage if not provided
            const paramsWithFees = {
                ...params,
                platformFeePercent: params.platformFeePercent || this.platformFeePercent
            };

            // Calculate fee amount
            const feePercent = parseFloat(paramsWithFees.platformFeePercent);
            const feeMultiplier = feePercent / 100;
            const inputAmount = parseFloat(paramsWithFees.amount);
            const feeAmount = inputAmount * feeMultiplier;
            const swapAmount = inputAmount - feeAmount;

            // Format fee amount
            const formattedFeeAmount = paramsWithFees.inputMint === SOL_MINT
                ? feeAmount.toFixed(9)
                : this.formatTokenAmount(feeAmount.toString());

            // If input amount is SOL, we need to calculate fee and adjust swap amount
            let amount: string;
            if (paramsWithFees.inputMint === SOL_MINT) {
                // Convert to lamports for the API
                amount = Math.floor(swapAmount * LAMPORTS_PER_SOL).toString();
            } else {
                // For token-to-X swaps, we need to convert to smallest units
                try {
                    // CRITICAL FIX: Get actual token decimals from token info instead of using hardcoded 6
                    // We need to get this from the token info service
                    let tokenDecimals = 9; // Use 9 as safer default (covers more tokens)

                    // Try to get the actual token decimals using the proper token info service
                    try {
                        // Import and use the getTokenInfo from solanatrackerService
                        const { getTokenInfo } = await import('./solanatrackerService');
                        const tokenInfo = await getTokenInfo(paramsWithFees.inputMint);

                        // Check if we have valid token info with decimals
                        if (tokenInfo && tokenInfo.status === 'success' && tokenInfo.data?.decimals !== undefined) {
                            tokenDecimals = tokenInfo.data.decimals;
                            console.log(`[QuoteAmount] Got actual token decimals from token info: ${tokenDecimals}`);
                        } else {
                            console.warn(`[QuoteAmount] Could not get decimals from token info, using default: ${tokenDecimals}`);
                        }
                    } catch (tokenInfoError) {
                        console.warn(`[QuoteAmount] Error getting token info, using default decimals: ${tokenDecimals}`, tokenInfoError);
                    }

                    console.log(`[QuoteAmount] Original string amount: ${paramsWithFees.amount}`);

                    // CORRECT CONVERSION: Jupiter expects an integer in the smallest units (base units)
                    const rawAmountString = paramsWithFees.amount;

                    try {
                        if (rawAmountString.includes('.')) {
                            // Example: "60920.693960326" with 6 decimals
                            const [wholePart, decimalPart] = rawAmountString.split('.');

                            // Pad decimal part with zeros if needed
                            const paddedDecimal = decimalPart.padEnd(tokenDecimals, '0').substring(0, tokenDecimals);

                            // Combine into an integer
                            // Result: "60920693960"
                            amount = `${wholePart}${paddedDecimal}`;

                            // Remove any leading zeros
                            amount = amount.replace(/^0+/, '') || '0';

                            console.log(`[QuoteAmount] Decimal conversion: ${rawAmountString} → ${amount} (with ${tokenDecimals} decimals)`);
                        } else {
                            // For whole numbers, multiply by 10^decimals
                            // Example: "60920" with 6 decimals = "60920000000"
                            amount = `${rawAmountString}${'0'.repeat(tokenDecimals)}`;

                            console.log(`[QuoteAmount] Whole number conversion: ${rawAmountString} → ${amount} (with ${tokenDecimals} decimals)`);
                        }

                        // Ensure it's a valid integer with no decimals
                        if (amount.includes('.')) {
                            // Remove any decimal part (shouldn't happen with our algorithm, but just in case)
                            amount = amount.split('.')[0];
                        }

                        // Final validation - ensure we have a valid integer string
                        BigInt(amount);
                    } catch (error) {
                        console.error('[QuoteAmount] Error in precise conversion, falling back to standard method:', error);

                        // Fallback to standard conversion
                        const amountNum = parseFloat(rawAmountString);
                        const multiplier = Math.pow(10, tokenDecimals);
                        const baseUnits = Math.floor(amountNum * multiplier);
                        amount = baseUnits.toString();
                    }

                    console.log(`[QuoteAmount] Final amount in smallest units for Jupiter: ${amount}`);
                } catch (error) {
                    console.error('Error converting token amount:', error);
                    // Use default 9 decimals as fallback (safer than 6)
                    const defaultDecimals = 9;
                    console.warn(`Using fallback with ${defaultDecimals} decimals for token ${paramsWithFees.inputMint}`);

                    // For fallback, use a different approach
                    const inputAmountNum = parseFloat(paramsWithFees.amount);
                    const inputAmountInSmallestUnits = Math.floor(inputAmountNum * Math.pow(10, defaultDecimals));
                    amount = inputAmountInSmallestUnits.toString();
                }
            }

            // Convert slippage percentage to basis points (1% = 100 bps)
            const slippageBps = (parseFloat(paramsWithFees.slippagePercent) * 100).toString();

            // Get quote
            const quoteResponse = await getJupiterQuote({
                inputMint: paramsWithFees.inputMint,
                outputMint: paramsWithFees.outputMint,
                amount,
                slippageBps
            });

            // Format output amount
            let estimatedOutputAmount = this.formatTokenAmount(quoteResponse.outAmount);

            // If outputMint is SOL, convert to SOL
            if (paramsWithFees.outputMint === SOL_MINT) {
                estimatedOutputAmount = (parseFloat(estimatedOutputAmount) / LAMPORTS_PER_SOL).toString();
            }

            // Return formatted quote result
            return {
                inputMint: paramsWithFees.inputMint,
                outputMint: paramsWithFees.outputMint,
                inputAmount: paramsWithFees.amount,
                swapAmount: swapAmount.toString(),
                estimatedOutputAmount,
                estimatedFeeAmount: formattedFeeAmount,
                priceImpact: quoteResponse.priceImpactPct ? quoteResponse.priceImpactPct.toString() : '0',
                platformFeePercent: paramsWithFees.platformFeePercent,
                routes: quoteResponse.routesInfos || []
            };
        } catch (error) {
            console.error('EKKO Trade Engine: Error getting quote', error);

            if (error instanceof Error && error.message.includes("Token not found in Jupiter")) {
                throw new TradeError(`This token is too new or not recognized by Jupiter`, TradeErrorType.TOKEN_NOT_FOUND, error);
            }

            throw new TradeError(
                `Failed to get quote: ${error instanceof Error ? error.message : 'Unknown error'}`,
                TradeErrorType.UNKNOWN_ERROR,
                error
            );
        }
    }

    /**
     * Format token amount to a displayable string
     */
    private formatTokenAmount(amount: string | number): string {
        const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

        if (isNaN(numericAmount)) {
            return '0';
        }

        if (numericAmount < 0.001) {
            return numericAmount.toExponential(4);
        } else if (numericAmount < 1) {
            return numericAmount.toFixed(6);
        } else if (numericAmount < 10000) {
            return numericAmount.toFixed(4);
        } else {
            return numericAmount.toLocaleString(undefined, { maximumFractionDigits: 2 });
        }
    }

    /**
     * Validate trade parameters
     */
    private validateTradeParams(params: TradeParams): void {
        // Check required fields
        if (!params.inputMint) throw new TradeError('Input mint is required', TradeErrorType.UNKNOWN_ERROR);
        if (!params.outputMint) throw new TradeError('Output mint is required', TradeErrorType.UNKNOWN_ERROR);
        if (!params.amount) throw new TradeError('Amount is required', TradeErrorType.UNKNOWN_ERROR);
        if (!params.userWalletAddress) throw new TradeError('User wallet address is required', TradeErrorType.UNKNOWN_ERROR);
        if (!params.tradingWalletAddress) throw new TradeError('Trading wallet address is required', TradeErrorType.UNKNOWN_ERROR);

        // Validate amounts
        const amount = parseFloat(params.amount);
        if (isNaN(amount) || amount <= 0) {
            throw new TradeError('Amount must be a positive number', TradeErrorType.UNKNOWN_ERROR);
        }

        // Validate slippage
        const slippage = parseFloat(params.slippagePercent);
        if (isNaN(slippage) || slippage < 0 || slippage > 100) {
            throw new TradeError('Slippage must be between 0 and 100', TradeErrorType.UNKNOWN_ERROR);
        }

        // Validate addresses
        try {
            new PublicKey(params.userWalletAddress);
            new PublicKey(params.tradingWalletAddress);
            new PublicKey(params.inputMint);
            new PublicKey(params.outputMint);
        } catch (error) {
            throw new TradeError('Invalid address format', TradeErrorType.UNKNOWN_ERROR, error);
        }
    }

    /**
     * Check if trading wallet has sufficient SOL balance for transaction fees and trade amount
     */
    private async checkTradingWalletBalance(tradingWalletAddress: string, tradeParams?: TradeParams): Promise<void> {
        try {
            const balance = await getBalance(tradingWalletAddress);
            const balanceInSol = balance / LAMPORTS_PER_SOL;

            // Base minimum required for transaction fees (0.002 SOL is usually sufficient)
            let requiredSol = 0.002;

            // If this is a SOL->Token trade, add the trade amount to required balance
            if (tradeParams && tradeParams.inputMint === SOL_MINT) {
                const tradeAmount = parseFloat(tradeParams.amount);
                requiredSol += tradeAmount;
                console.log(`🔍 SOL->Token trade detected: need ${tradeAmount} SOL for trade + ${0.005} SOL for fees = ${requiredSol} SOL total`);
            }

            console.log(`💰 Balance check: Have ${balanceInSol.toFixed(6)} SOL, need ${requiredSol.toFixed(6)} SOL`);

            if (balanceInSol < requiredSol) {
                const shortfall = requiredSol - balanceInSol;
                throw new TradeError(
                    `Trading wallet has insufficient SOL. Current balance: ${balanceInSol.toFixed(6)} SOL, required: ${requiredSol.toFixed(6)} SOL (shortfall: ${shortfall.toFixed(6)} SOL)`,
                    TradeErrorType.INSUFFICIENT_BALANCE
                );
            }

            console.log(`✅ Balance check passed: ${balanceInSol.toFixed(6)} SOL >= ${requiredSol.toFixed(6)} SOL required`);
        } catch (error) {
            if (error instanceof TradeError) {
                throw error;
            }

            throw new TradeError(
                `Error checking trading wallet balance: ${error instanceof Error ? error.message : 'Unknown error'}`,
                TradeErrorType.UNKNOWN_ERROR,
                error
            );
        }
    }

    /**
     * Trigger an immediate balance refresh for a wallet
     */
    private triggerBalanceRefresh(walletAddress: string): void {
        // Dispatch wallet_selected event to trigger immediate balance refresh
        window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
            detail: {
                walletPublicKey: walletAddress
            }
        }));
        console.log(`EKKO Trade Engine: Triggered immediate balance refresh for wallet ${walletAddress}`);
    }

    /**
     * Log a trade to the unified trades table
     */
    private async logTrade(params: TradeParams, result: TradeResult): Promise<void> {
        try {
            // Determine the token address (non-SOL mint)
            const tokenAddress = params.inputMint === SOL_MINT ? params.outputMint : params.inputMint;
            const tradeType = params.inputMint === SOL_MINT ? 'buy' : 'sell';

            // Get token info and market data for logging
            const tokenAndMarketData = await getTokenAndMarketDataForLogging(tokenAddress);

            // Determine trade method from context
            const tradeMethod = determineTradeMethod(params.tradeContext);

            // Calculate SOL amount involved
            const solAmount = params.inputMint === SOL_MINT
                ? params.amount
                : result.estimatedOutputAmount || '0';

            const tradeLogEntry: TradeLogEntry = {
                userWalletAddress: params.userWalletAddress,
                tradingWalletAddress: params.tradingWalletAddress,
                userId: params.userId, // Pass user ID if provided
                transactionSignature: result.signature,
                tradeMethod,
                tradeType,

                tokenAddress,
                tokenSymbol: tokenAndMarketData.symbol,
                tokenName: tokenAndMarketData.name,

                inputMint: params.inputMint,
                outputMint: params.outputMint,
                inputAmount: params.amount,
                outputAmount: result.estimatedOutputAmount,
                solAmount,

                // Market data at execution time
                tokenPriceUsd: tokenAndMarketData.tokenPriceUsd,
                tokenPriceSol: tokenAndMarketData.tokenPriceSol,
                marketCapUsd: tokenAndMarketData.marketCapUsd,
                marketCapSol: tokenAndMarketData.marketCapSol,
                liquidityUsd: tokenAndMarketData.liquidityUsd,
                volume24hUsd: tokenAndMarketData.volume24hUsd,

                slippagePercent: params.slippagePercent,
                priorityFeeLamports: params.priorityFee,
                platformFeeSol: result.fees?.platformFee?.toString(),
                networkFeeSol: result.fees?.networkFee?.toString(),

                status: result.status === TransactionStatus.CONFIRMED ? 'confirmed' :
                    result.status === TransactionStatus.FAILED ? 'failed' : 'pending',

                ruleId: params.tradeContext?.ruleId,
                ruleName: params.tradeContext?.ruleName,
                metadata: params.tradeContext?.metadata
            };

            // Log the trade (don't await to avoid blocking)
            tradeLoggingService.logTrade(tradeLogEntry).catch(error => {
                console.error('Failed to log trade:', error);
            });

        } catch (error) {
            console.error('Error preparing trade log:', error);
        }
    }
}

// Export a singleton instance for easy access
export const tradeEngine = EkkoTradeEngine.getInstance();

// Simplified function for backward compatibility with existing code
export async function buySolToken(
    outputMint: string,
    amountInSol: string,
    slippagePercent: string,
    userWalletAddress: string,
    tradingWalletAddress: string
): Promise<{ signature: string }> {
    try {
        // Validate that we have the required trading wallet address
        if (!tradingWalletAddress) {
            throw new Error("Please activate a trading wallet in your profile.");
        }

        // Use the tradeEngine singleton to execute the swap with platform fees
        const result = await tradeEngine.executeSwap({
            inputMint: SOL_MINT,
            outputMint,
            amount: amountInSol,
            userWalletAddress,
            tradingWalletAddress,
            slippagePercent
        });

        return { signature: result.signature };
    } catch (error) {
        if (error instanceof TradeError) {
            throw new Error(error.message);
        }
        throw error;
    }
}